import { Injectable } from '@angular/core';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import {
  ClientActionTypes,
  ClientsPageRequested,
  ClientsPageLoaded,
  ClientCreated,
  ClientDeleted,
  ClientUpdated,
  ClientOnServerCreated,
  ClientsActionToggleLoading,
  ClientsPageToggleLoading,
  ThrowClientError,
  FireClientSuccess,
  ClientOnServerUpdated,
  ClientsPageCancelled,
  ClientOnServerDeleted,
} from '../_actions/client.actions';
import { Router } from '@angular/router';
import { ClientService } from '../_services';
import { AuthService } from '../../auth/_services';
import { QueryParamsModel } from '../../_base/crud';

@Injectable()
export class ClientEffects {
  showPageLoadingDistpatcher = new ClientsPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new ClientsPageToggleLoading({ isLoading: false });
  showActionLoadingDistpatcher = new ClientsActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new ClientsActionToggleLoading({ isLoading: false });

  @Effect()
  loadClientsPage$ = this.actions$.pipe(
    ofType<ClientsPageRequested>(ClientActionTypes.ClientsPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.client.findClients(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin([requestToServer, lastQuery]).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0] && response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new ClientsPageCancelled();
            } else {
              this.store.dispatch(new ThrowClientError({ error: response[0].error.error }));
              return new ClientsPageCancelled();
            }
          } else {
            const result = response[0];
            // tslint:disable-next-line:no-shadowed-variable
            const lastQuery: QueryParamsModel = response[1];
            return new ClientsPageLoaded({
              clients: result.data,
              totalCount: result.count,
              page: lastQuery,
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deleteClient$ = this.actions$.pipe(
    ofType<ClientOnServerDeleted>(ClientActionTypes.ClientOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.client.deleteClient(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowClientError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireClientSuccess({ action_type: ClientActionTypes.ClientDeleted }),
            );
            return new ClientDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updateClient$ = this.actions$.pipe(
    ofType<ClientOnServerUpdated>(ClientActionTypes.ClientOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.client.updateClient(payload.client).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowClientError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireClientSuccess({ action_type: ClientActionTypes.ClientUpdated }),
            );
            return new ClientUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createClient$ = this.actions$.pipe(
    ofType<ClientOnServerCreated>(ClientActionTypes.ClientOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.client.createClient(payload.client).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowClientError({ error: res.error });
          } else {
            this.store.dispatch(
              new FireClientSuccess({ action_type: ClientActionTypes.ClientCreated }),
            );
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new ClientCreated({ client: res });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private client: ClientService,
    private store: Store<AppState>,
    private router: Router,
    private authService: AuthService,
  ) {}
}
